import moment from 'moment';

import { URLS } from '../../constants';
import apiClient from '../client';

const sessionApis = {
  createSession: ({
    name,
    createdFor,
    createdBy,
    modifiedBy,
    programBookUUID,
    target,
  }: {
    name: string;
    createdFor: string;
    createdBy: number;
    modifiedBy: number;
    programBookUUID: string;
    target: any;
  }) => {
    const data = {
      name,
      createdFor,
      createdBy,
      modifiedBy,
      programBookUUID,
      target,
    };
    return apiClient.post(URLS.createSession, {
      data: {
        ...data,
      },
    });
  },
  checkDuplicateName: ({ name }: { name: string }) => {
    return apiClient.post(URLS.checkDuplicateSessionName, {
      data: {
        modifiedSessionName: name,
      },
    });
  },
  getSessionTargets: ({ sessionId, type }: { sessionId: string; type: string }) => {
    return apiClient.post(URLS.getSessionTargets, {
      data: {
        sessionId,
        type,
        startTime: moment().utc().format('YYYY-MM-DDTHH:mm:ss'),
        endTime: moment().subtract(1, 'day').utc().format('YYYY-MM-DDTHH:mm:ss'),
      },
    });
  },
  runSession: ({
    sessionId,
    createdBy,
    modifiedBy,
    startTime,
    modifiedDate,
    isManualDataInsertion,
    ranBy,
  }: {
    sessionId: string;
    createdBy: number;
    modifiedBy: number;
    startTime: string;
    modifiedDate?: string;
    isManualDataInsertion?: boolean;
    ranBy?: string;
  }) => {
    return apiClient.post(URLS.runSession, {
      data: {
        sessionId,
        createdBy,
        modifiedBy,
        startTime,
        modifiedDate,
        isManualDataInsertion,
        ranBy,
      },
    });
  },
  getTrialsByTarget: ({
    sessionRunId,
    targetId,
    phase,
  }: {
    sessionRunId: string;
    targetId: string;
    phase: string;
  }) => {
    return apiClient.post(URLS.getTrialsByTarget, {
      data: {
        sessionRunId,
        targetId,
        phase,
      },
    });
  },
  getTrialsByTargetForTaskAnalysis: ({
    sessionRunId,
    targetId,
    phase,
  }: {
    sessionRunId: string;
    targetId: string;
    phase: string;
  }) => {
    return apiClient.post(URLS.getTrialsByTargetForTaskAnalysis, {
      data: {
        sessionRunId,
        targetId,
        phase,
      },
    });
  },
  saveTrial: ({
    commentsId,
    sessionId,
    targetId,
    comments,
    prompts,
    frequency,
    phase,
    trailCount,
    duration,
    createdBy,
    modifiedBy,
    sessionRunId,
    phaseCount,
    trialId = '',
    positiveCount,
    negativeCount,
    modifiedDate,
    isManualDataInsertion,
    ranBy,
    sessionInitiated = false,
    editingManually,
  }: {
    commentsId: string;
    sessionId: string;
    targetId: string;
    comments: string;
    prompts: string;
    frequency: string;
    phase: string;
    trailCount: string;
    duration: string;
    createdBy: number;
    modifiedBy: number;
    sessionRunId: string;
    phaseCount: string;
    trialId?: string;
    positiveCount: string;
    negativeCount: string;
    modifiedDate?: string;
    isManualDataInsertion?: boolean;
    ranBy?: string;
    sessionInitiated?: boolean;
    editingManually?: boolean;
  }) => {
    return apiClient.post(URLS.saveTrial, {
      data: {
        commentsId,
        sessionId,
        targetId,
        comments,
        prompts,
        phase,
        trailCount,
        duration,
        createdBy,
        modifiedBy,
        sessionRunId,
        trialId,
        frequency,
        phaseCount,
        positiveCount,
        negativeCount,
        modifiedDate,
        isManualDataInsertion,
        ranBy,
        sessionInitiated,
        editingManually,
      },
    });
  },
  getSessionById: ({ sessionId }: { sessionId: string }) => {
    return apiClient.post(URLS.getSessionById, {
      data: {
        sessionId,
      },
    });
  },
  endSessionInterrupted: ({ sessionRunId }: { sessionRunId: string }) => {
    return apiClient.post(URLS.endSessionInterrupted, {
      data: {
        sessionRunId,
      },
    });
  },
  endSession: ({
    sessionRunId,
    duration,
    endTime,
    modifiedDate,
    isManualDataInsertion,
    ranBy,
    editingManually,
  }: {
    sessionRunId: string;
    duration: string;
    endTime: string;
    modifiedDate?: string;
    isManualDataInsertion?: boolean;
    ranBy?: string;
    editingManually?: boolean;
  }) => {
    return apiClient.post(URLS.endSession, {
      data: {
        sessionRunId,
        duration,
        endTime,
        modifiedDate,
        isManualDataInsertion,
        ranBy,
        editingManually,
      },
    });
  },
  getAllTargetsBySession: ({ sessionId }: { sessionId: string }) => {
    return apiClient.post(URLS.getAllTargetsBySession, {
      data: {
        sessionId,
      },
    });
  },
  getTargetRunSummary: ({ sessionRunId }: { sessionRunId: string }) => {
    return apiClient.post(URLS.getTargetRunSummary, {
      data: {
        sessionRunId,
      },
    });
  },
  editSession: ({
    sessionId,
    name,
    modifiedBy,
    target,
  }: {
    sessionId: string;
    name: string;
    modifiedBy: number;
    target: any;
  }) => {
    return apiClient.post(URLS.editSession, {
      data: {
        sessionId,
        name,
        modifiedBy,
        target,
      },
    });
  },
  deleteSession: ({
    sessionNoteDataId,
    isFromExistingNote,
  }: {
    sessionNoteDataId: any;
    isFromExistingNote: any;
  }) => {
    return apiClient.post(URLS.deleteSession, {
      data: {
        sessionNoteDataId,
        isFromExistingNote,
      },
    });
  },
  deleteSessionById: ({ id }: { id: any }) => {
    return apiClient.post(URLS.deleteSessionById, {
      data: {
        id,
      },
    });
  },
  fetchSessionsByClientId: ({ clientId, targetId }: { clientId?: string; targetId?: string }) => {
    return apiClient.post(URLS.fetchSessionsByClientId, {
      data: {
        clientId,
        targetId,
      },
    });
  },
  fetchSelectedSummary: ({
    clientId,
    date,
    providerId,
  }: {
    clientId?: string;
    date?: any;
    providerId?: any;
  }) => {
    return apiClient.post(URLS.fetchSessionSummary, {
      data: {
        clientId,
        date,
        providerId,
      },
    });
  },
  getAllABCDropdownData: () => {
    return apiClient.post(URLS.getAllABCDropdownData, {
      data: {},
    });
  },
  saveABCSessionData: (data: any) => {
    return apiClient.post(URLS.saveABCSessionData, {
      data: {
        data,
      },
    });
  },
  getABCSessionData: (sessionId: string, sessionRunId: string) => {
    return apiClient.post(URLS.getABCSessionData, {
      data: {
        sessionId,
        sessionRunId,
      },
    });
  },
  sessionHistory: ({ sessionId }: { sessionId: any }) => {
    return apiClient.post(URLS.sessionHistory, {
      data: {
        sessionId,
      },
    });
  },
  saveTrialForTaskAnalysis: ({
    commentsId,
    sessionId,
    targetId,
    comments,
    phase,
    trailCount,
    userId,
    createdBy,
    modifiedBy,
    sessionRunId,
    trialId,
    response,
    modifiedDate,
    isManualDataInsertion,
    ranBy,
    editingManually,
  }: {
    commentsId: string;
    sessionId: string;
    targetId: string;
    comments: string;
    phase: string;
    trailCount: string;
    userId: string;
    createdBy: string;
    modifiedBy: string;
    sessionRunId: string;
    trialId: string;
    response: any;
    modifiedDate?: string;
    isManualDataInsertion?: boolean;
    ranBy?: string;
    editingManually?: boolean;
  }) => {
    return apiClient.post(URLS.saveTrialForTaskAnalysis, {
      data: {
        commentsId,
        sessionId,
        targetId,
        comments,
        phase,
        trailCount,
        userId,
        createdBy,
        modifiedBy,
        sessionRunId,
        trialId,
        response,
        modifiedDate,
        isManualDataInsertion,
        ranBy,
        editingManually,
      },
    });
  },
  bulkTerminateSessions: (sessionRunIds: string[]) => {
    return apiClient.post(URLS.terminateBulkSessions, {
      data: {
        sessionRunIds,
      },
    });
  },
  getLastThreePrompts: (sessionId: string, targetId: string) => {
    return apiClient.post(URLS.getLastThreePrompts, {
      data: {
        sessionId,
        targetId,
      },
    });
  },
  getSessionRunDetails: (sessionRunId: string) => {
    return apiClient.post(URLS.getSessionRunDetails, {
      data: {
        sessionRunId,
      },
    });
  },
  globalSearch: (searchString: string, dateFilters: any, type: string = 'All') => {
    let dateRange = null;
    if (Object.keys(dateFilters).length) {
      dateRange = {
        startDate: dateFilters[0].filterValue,
        endDate: dateFilters[1].filterValue,
      };
    }
    return apiClient.post(URLS.globalSessionsSearch, {
      data: {
        searchString,
        ...dateRange,
        type,
      },
    });
  },
  getSessionRunStatus: (sessionId: string) => {
    return apiClient.post(URLS.getSessionRunStatus, {
      data: {
        sessionId,
      },
    });
  },
  getUnterminatedSessions: (sessionId: string) => {
    return apiClient.post(URLS.getUnterminatedSessions, {
      data: {
        sessionId,
      },
    });
  },
  undoTrial: ({
    sessionId,
    sessionRunId,
    targetId,
    trialId,
  }: {
    sessionId: string;
    sessionRunId: string;
    targetId: string;
    trialId: string;
  }) => {
    return apiClient.post(URLS.undoTrial, {
      data: {
        sessionId,
        sessionRunId,
        targetId,
        trialId,
      },
    });
  },
  getLastThreeTargetDataPointByTargetId: (targetId: number) => {
    return apiClient.post(URLS.getLastThreeTargetDataPointByTargetId, {
      data: {
        targetId,
      },
    });
  },
};
export default sessionApis;
